import { Col, InputNumber, Row } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { t } from "i18next";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button } from "../../../../../../../components/Button/Button";
import {
  Select,
  SelectOption,
} from "../../../../../../../components/Select/Select";
import { ExciseStampsContext } from "../../../../../../../modules/obligee/exciseStamps/exciseStamps.context";
import {
  IKategorijaZaZahtevProps,
  IPodaciOTrazenimMarkicama,
  IPodaciOTrazenimMarkicamaPrint,
} from "../../../../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { isDivisible } from "../../../../../../../utils/functions";
import style from "./ChooseRequestedStampsRow.module.scss";
const DEFAULT_MINIMAL_QUANTITY = 50_000;

interface ILayoutProps {
  title: string;
  isStampAdded: boolean;
  handleResetIsStampAdded: () => void;
}

const Layout: React.FC<ILayoutProps> = ({
  title,
  handleResetIsStampAdded,
  isStampAdded,
}) => {
  const { exciseStamps, setExciseStamps } = useContext(ExciseStampsContext);
  const [isTipPakovanjaDisabled, setIsTipPakovanjaDisabled] = useState(false);

  const setOznaka = (oznakaId: number, option: any) => {
    setExciseStamps({
      selectedOznakaPakovanjaId: oznakaId,
      selectedOznakaPakovanjaOpis: option.children,
    });
  };

  const setVrsta = (
    vrstaId: number,
    option: DefaultOptionType | DefaultOptionType[]
  ) => {
    const singleOption = option as DefaultOptionType;

    const preselectedTipPakovanja =
      exciseStamps.kategorijeZaZahtev.tipPakovanja?.find(
        (el) => el.id === singleOption?.children?.[0]?.label
      );

    if (preselectedTipPakovanja) {
      setIsTipPakovanjaDisabled(true);

      setExciseStamps({
        tipPakovanjaId: preselectedTipPakovanja?.id,
        tipPakovanja: preselectedTipPakovanja?.naziv,
        tipPakovanjaShow: true,
      });
    } else {
      setIsTipPakovanjaDisabled(false);
    }

    setExciseStamps({
      selectedVrstaAkcizneMarkiceId: vrstaId,
      selectedVrstaAkcizneMarkiceIme: singleOption?.label as string,
    });
  };

  const setKolicina = (value: any) => {
    setExciseStamps({ selectedTrazeneKolicine: value });
  };

  const handleTipPakovanjaChange = (
    value: string,
    option: DefaultOptionType | DefaultOptionType[]
  ) => {
    if (Array.isArray(option)) {
      return;
    }

    setExciseStamps({
      tipPakovanjaId: parseInt(value),
      tipPakovanja: option.label as string,
      tipPakovanjaShow: true,
    });
  };

  const isVrstaAkcizneMarkiceDisabled = (markica: IKategorijaZaZahtevProps) => {
    return Boolean(
      exciseStamps.podaciOTrazenimMarkicama?.find(
        (m) => m.markicaKategorijaVrsteId === markica.markicaKategorijaVrsteId
      ) ||
        exciseStamps.podaciOTrazenimMarkicamaPrint?.find(
          (m) => m.markicaKategorijeVrsteId === markica.markicaKategorijaVrsteId
        )
    );
  };

  const vrsteAkiznihMarkica = useMemo(() => {
    return exciseStamps.kategorijeZaZahtev.kategorijaZaZahtev?.map((vrsta) => ({
      value: vrsta.markicaKategorijaVrsteId,
      label: vrsta.markicaKategorijeVrsteIme,
      children: vrsta.preselectedTipPakovanjaIds?.map((el) => ({
        label: el,
      })),
      disabled: isVrstaAkcizneMarkiceDisabled(vrsta),
    }));
  }, [
    exciseStamps.kategorijeZaZahtev.kategorijaZaZahtev,
    exciseStamps.podaciOTrazenimMarkicama,
  ]);

  const quantityErrorMessage = useMemo(() => {
    if (
      parseInt(exciseStamps.selectedTrazeneKolicine) !== 0 &&
      !exciseStamps.selectedTrazeneKolicine
    ) {
      return "";
    }

    const minimalQuantity =
      exciseStamps.kategorijeZaZahtev.tipPakovanja.find(
        (tipPakovanja) => tipPakovanja.id === exciseStamps.tipPakovanjaId
      )?.minimalnaKolicina || DEFAULT_MINIMAL_QUANTITY;

    const isLessThanMinimal =
      parseInt(exciseStamps.selectedTrazeneKolicine) < minimalQuantity;

    const isDivisibleByMinimalQuantity = isDivisible({
      dividend: parseInt(exciseStamps.selectedTrazeneKolicine),
      divisor: minimalQuantity,
    });

    if (isLessThanMinimal) {
      return `${t(
        "AKCIZNE_MARKICE.MINIMALNA_KOLICINA_ZA_IZABRANU_VELICINU_PAKOVANJA_JE"
      )}: ${minimalQuantity}`;
    }

    if (!isDivisibleByMinimalQuantity) {
      return `${t(
        "AKCIZNE_MARKICE.KOLICINA_MARKICA_MORA_BITI_DELJIVA_SA_MINIMALNOM_KOLICINOM"
      )}: ${minimalQuantity}.`;
    }

    return "";
  }, [
    exciseStamps.kategorijeZaZahtev,
    exciseStamps.tipPakovanjaId,
    exciseStamps.selectedTrazeneKolicine,
  ]);

  useEffect(() => {
    if (isStampAdded) {
      setIsTipPakovanjaDisabled(false);
      handleResetIsStampAdded();
    }
  }, [isStampAdded]);

  if (["kafu", "кафу", "kafa", "кафa"].includes(title)) {
    return (
      <>
        <Col className={`${style.fontSize} pr-40`} span={5}>
          <Select
            name="vrsta-akcizne-markice"
            className={style.fontSize}
            popupClassName={style.fontSize}
            value={exciseStamps.selectedVrstaAkcizneMarkiceId}
            onChange={setVrsta}
            options={vrsteAkiznihMarkica}
            placeholder={t("AKCIZNE_MARKICE.VRSTE_AKCIZNE_MARKICE")}
          />
        </Col>
        <Col className={`${style.fontSize} pr-40`} span={5}>
          <Select
            name="oznaka-pakovanja-akcizne-markice"
            className={style.fontSize}
            value={exciseStamps.selectedOznakaPakovanjaId}
            onChange={setOznaka}
          >
            {exciseStamps.kategorijeZaZahtev.pakovanjaZaZahtev.map(
              (pakovanje, i) => {
                return (
                  <SelectOption
                    key={i}
                    className={style.fontSize}
                    value={pakovanje.pakovanjeId}
                  >
                    {pakovanje.pakovanjeOpis}
                  </SelectOption>
                );
              }
            )}
          </Select>
        </Col>
        {exciseStamps.kategorijeZaZahtev.tipPakovanjaShow && (
          <Col className={`${style.fontSize} pr-40`} span={5}>
            <Select
              name="tip-pakovanja-akcizne-markice"
              className={style.fontSize}
              value={exciseStamps.tipPakovanjaId}
              popupClassName={style.fontSize}
              options={exciseStamps.kategorijeZaZahtev?.tipPakovanja?.map(
                (tip) => ({ value: tip.id, label: tip.naziv })
              )}
              onChange={handleTipPakovanjaChange}
              placeholder={t(
                "AKCIZNE_MARKICE.IZABERITE_VELICINU_PAKOVANJA_MARKICE"
              )}
              disabled={
                isTipPakovanjaDisabled && !!exciseStamps?.tipPakovanjaId
              }
            />
          </Col>
        )}
        <Col className={`${style.fontSize} pr-40`} span={5}>
          <InputNumber
            min="0"
            name="trazene-kolicine-akcizne-markice"
            onChange={setKolicina}
            value={exciseStamps.selectedTrazeneKolicine}
            status={quantityErrorMessage && "error"}
          />
          <p className={style.fontSize} style={{ color: "red" }}>
            {quantityErrorMessage}
          </p>
        </Col>
      </>
    );
  }

  return (
    <>
      <Col className={`${style.fontSize} pr-40`} span={8}>
        <Select
          name="vrsta-akcizne-markice"
          className={style.fontSize}
          popupClassName={style.fontSize}
          value={exciseStamps.selectedVrstaAkcizneMarkiceId}
          options={vrsteAkiznihMarkica}
          onChange={setVrsta}
          placeholder={t("AKCIZNE_MARKICE.VRSTE_AKCIZNE_MARKICE")}
        />
      </Col>
      {exciseStamps.kategorijeZaZahtev.tipPakovanjaShow && (
        <Col className={`${style.fontSize} pr-40`} span={6}>
          <Select
            name="tip-pakovanja-akcizne-markice"
            className={style.fontSize}
            value={exciseStamps.tipPakovanjaId}
            options={exciseStamps.kategorijeZaZahtev?.tipPakovanja?.map(
              (tip) => ({ value: tip.id, label: tip.naziv })
            )}
            onChange={handleTipPakovanjaChange}
            popupClassName={style.fontSize}
            placeholder={t(
              "AKCIZNE_MARKICE.IZABERITE_VELICINU_PAKOVANJA_MARKICE"
            )}
            disabled={isTipPakovanjaDisabled && !!exciseStamps?.tipPakovanjaId}
          />
        </Col>
      )}
      <Col className={`${style.fontSize} pr-40`} span={6}>
        <InputNumber
          min="0"
          className={style.fontSize}
          name="trazene-kolicine-akcizne-markice"
          value={exciseStamps.selectedTrazeneKolicine}
          onChange={setKolicina}
          status={quantityErrorMessage && "error"}
        />
        <p className={style.fontSize} style={{ color: "red" }}>
          {quantityErrorMessage}
        </p>
      </Col>
    </>
  );
};

interface IChooseRequestedStampsRowProps {
  title: string;
}

export const ChooseRequestedStampsRow: React.FC<
  IChooseRequestedStampsRowProps
> = ({ title }) => {
  const { exciseStamps, setExciseStamps } = useContext(ExciseStampsContext);
  const [isStampAdded, setIsStampAdded] = useState(false);

  const addStamp = () => {
    const newStampPayload: IPodaciOTrazenimMarkicama = {
      markicaKategorijaVrsteId: exciseStamps.selectedVrstaAkcizneMarkiceId,
      markicaVrstaPakovanjaId: exciseStamps.selectedOznakaPakovanjaId,
      kolicina: Number(exciseStamps.selectedTrazeneKolicine),
      isporuceniOpsezi: exciseStamps.isporuceniOpsezi,
      nedostajuceVrednosti: exciseStamps.selectedNedostajuceVrednosti,
      tipPakovanjaId: exciseStamps.tipPakovanjaId,
    };

    const newStampPrint: IPodaciOTrazenimMarkicamaPrint = {
      markicaKategorijeVrsteIme: exciseStamps.selectedVrstaAkcizneMarkiceIme,
      markicaKategorijeVrsteId: exciseStamps.selectedVrstaAkcizneMarkiceId,
      pakovanjeOpis: exciseStamps.selectedOznakaPakovanjaOpis,
      kolicina: Number(exciseStamps.selectedTrazeneKolicine),
      isporuceniOpsezi: exciseStamps.isporuceniOpsezi,
      nedostajuceVrednosti: exciseStamps.selectedNedostajuceVrednosti,
      tipPakovanja: exciseStamps.tipPakovanja,
      tipPakovanjaShow: exciseStamps.tipPakovanjaShow,
    };

    setExciseStamps({
      selectedVrstaAkcizneMarkiceId: null,
      selectedOznakaPakovanjaId: null,
      selectedTrazeneKolicine: "",
      tipPakovanjaId: null,
      podaciOTrazenimMarkicama: [
        ...exciseStamps.podaciOTrazenimMarkicama,
        newStampPayload,
      ],
      podaciOTrazenimMarkicamaPrint: [
        ...exciseStamps.podaciOTrazenimMarkicamaPrint,
        newStampPrint,
      ],
    });

    setIsStampAdded(true);
  };

  const isQuantityInvalid = useMemo(() => {
    const minimalQuantity =
      exciseStamps.kategorijeZaZahtev.tipPakovanja.find(
        (tipPakovanja) => tipPakovanja.id === exciseStamps.tipPakovanjaId
      )?.minimalnaKolicina || DEFAULT_MINIMAL_QUANTITY;

    const isDivisibleByMinimalQuantity = isDivisible({
      dividend: parseInt(exciseStamps.selectedTrazeneKolicine),
      divisor: minimalQuantity,
    });

    return (
      parseInt(exciseStamps.selectedTrazeneKolicine) < minimalQuantity ||
      !isDivisibleByMinimalQuantity
    );
  }, [
    exciseStamps.kategorijeZaZahtev,
    exciseStamps.tipPakovanjaId,
    exciseStamps.selectedTrazeneKolicine,
  ]);

  const isAddStampDisabledAlkolohDuvan = !(
    exciseStamps.selectedTrazeneKolicine &&
    exciseStamps.selectedVrstaAkcizneMarkiceId
  );

  const isAddStampDisabledForKafa = !(
    exciseStamps.selectedTrazeneKolicine &&
    exciseStamps.selectedVrstaAkcizneMarkiceId &&
    exciseStamps.selectedOznakaPakovanjaId
  );

  const isAddStampDisabled =
    title === t("AKCIZNE_MARKICE.KAFA")
      ? isAddStampDisabledForKafa
      : isAddStampDisabledAlkolohDuvan;

  const handleResetIsStampAdded = () => {
    setIsStampAdded(false);
  };

  return (
    <Row className="pb-10">
      <Layout
        title={title}
        isStampAdded={isStampAdded}
        handleResetIsStampAdded={handleResetIsStampAdded}
      />
      <Col span={3}>
        <Button
          type="primary"
          onClick={addStamp}
          disabled={isAddStampDisabled || isQuantityInvalid}
        >
          {t("AKCIZNE_MARKICE.DODAJ")}
        </Button>
      </Col>
    </Row>
  );
};
